import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import api from '../../api/api';
import { formatDate } from '../../utils/dateUtils';
import EditVerseRangeModal from '../../components/learning-plans/EditVerseRangeModal';
import QuranService from '../../services/quranService';
import '../../styles/CreateAssignmentPage.css';

const AssignmentPreviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from location state (planData not plan - it's not saved yet)
  const locationPlanData = location.state?.planData;
  const locationAssignments = location.state?.suggestedAssignments || [];
  const locationWarnings = location.state?.warnings || [];
  const locationStudentName = location.state?.studentName || '';

  // State
  const [planData, setPlanData] = useState(locationPlanData);
  const [assignments, setAssignments] = useState(locationAssignments);
  const [warnings] = useState(locationWarnings);
  const [studentName] = useState(locationStudentName);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!locationPlanData);
  const [error, setError] = useState('');
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [isAddingAssignment, setIsAddingAssignment] = useState(false);

  // Check if plan data exists (required for preview)
  useEffect(() => {
    // If we have data from location state, we're good
    if (locationPlanData && locationAssignments.length > 0) {
      setInitialLoading(false);
      return;
    }

    // Otherwise, redirect back to create page
    // Note: Preview page requires location state (plan not saved yet, assignments generated during creation)
    // If user refreshes this page, data is lost and must be regenerated
    setInitialLoading(true);
    setError('Preview data lost. Please create the plan again to generate assignments.');
    // Redirect to plans list after 3 seconds
    setTimeout(() => navigate('/learning-plans'), 3000);
    setInitialLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationPlanData, navigate]);


  // Handle assignment edit
  const handleEditAssignment = (index, field, value) => {
    // Validate date changes
    if (field === 'dueDate') {
      const newDate = new Date(value);
      const planStart = new Date(planData.timeline.start);
      const planEnd = new Date(planData.timeline.end);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (newDate < today) {
        alert('Cannot set due date in the past');
        return;
      }

      if (newDate < planStart) {
        alert(`Due date cannot be before plan start date (${formatDate(planStart)})`);
        return;
      }

      // If date is after plan end, offer to extend the plan
      if (newDate > planEnd) {
        const extend = window.confirm(
          `Assignment due date (${formatDate(newDate)}) is after plan end date (${formatDate(planEnd)}).\n\nWould you like to extend the plan end date to ${formatDate(newDate)}?`
        );

        if (extend) {
          // Update planData in memory (plan not saved yet, so this is safe)
          setPlanData({
            ...planData,
            timeline: {
              ...planData.timeline,
              end: newDate
            }
          });
        } else {
          // User declined to extend plan - don't save the date change
          return;
        }
      }
    }

    const updatedAssignments = [...assignments];
    updatedAssignments[index] = {
      ...updatedAssignments[index],
      [field]: value
    };
    setAssignments(updatedAssignments);
  };

  // Handle assignment removal
  const handleRemoveAssignment = (index) => {
    if (window.confirm('Are you sure you want to remove this assignment?')) {
      const updatedAssignments = assignments.filter((_, i) => i !== index);
      setAssignments(updatedAssignments);
    }
  };

  // Handle edit verse range
  const handleEditVerseRange = (index) => {
    setEditingAssignment({ index, assignment: assignments[index] });
  };

  // Handle save verse range
  const handleSaveVerseRange = (updatedAssignment) => {
    const updatedAssignments = [...assignments];
    updatedAssignments[editingAssignment.index] = updatedAssignment;
    setAssignments(updatedAssignments);
    setEditingAssignment(null);
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingAssignment(null);
  };

  // Handle add assignment
  const handleAddAssignment = () => {
    setIsAddingAssignment(true);
  };

  // Handle save new assignment
  const handleSaveNewAssignment = (newAssignment) => {
    // Calculate default due date (last assignment's date + 1 day, or plan start)
    let defaultDueDate;
    if (assignments.length > 0) {
      const lastAssignment = assignments[assignments.length - 1];
      defaultDueDate = new Date(lastAssignment.dueDate);
      defaultDueDate.setDate(defaultDueDate.getDate() + 1);

      // Ensure date is not in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (defaultDueDate < today) {
        defaultDueDate = today;
      }
    } else {
      defaultDueDate = new Date(planData.timeline.start);
    }

    // Validate due date within plan timeline
    const planEnd = new Date(planData.timeline.end);
    if (defaultDueDate > planEnd) {
      defaultDueDate = planEnd;
    }

    // Add due date to the new assignment
    const assignmentWithDate = {
      ...newAssignment,
      dueDate: defaultDueDate
    };

    setAssignments([...assignments, assignmentWithDate]);
    setIsAddingAssignment(false);
  };

  // Handle cancel add assignment
  const handleCancelAddAssignment = () => {
    setIsAddingAssignment(false);
  };

  // Handle approval and create plan + assignments
  const handleApprove = async () => {
    if (assignments.length === 0) {
      setError('At least one assignment is required');
      return;
    }

    if (!planData.halaqaId) {
      setError('Class selection is required. Please go back and select a class.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send halaqaId both in planData and as separate parameter
      // - In planData: Required for plan creation (saved to database)
      // - As separate param: Used for assignment creation and validation
      const response = await api.post('/learning-plans/approve-assignments', {
        planData, // Keep halaqaId in planData
        halaqaId: planData.halaqaId, // Also send separately for validation
        assignments
      });

      if (response.data.success) {
        // Navigate to plan details page with success message (plan is now created)
        const createdPlanId = response.data.data.planId;
        navigate(`/learning-plans/${createdPlanId}`, {
          state: {
            successMessage: `Successfully created learning plan with ${response.data.data.assignmentsCreated} assignments`
          }
        });
      }
    } catch (error) {
      console.error('Error approving assignments:', error);
      setError(error.response?.data?.message || 'Failed to create assignments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get assignment content display
  const getAssignmentContent = (assignment) => {
    if (assignment.pages) {
      if (assignment.pages.from === assignment.pages.to) {
        return `Page ${assignment.pages.from}`;
      }
      return `Pages ${assignment.pages.from}-${assignment.pages.to}`;
    }

    if (assignment.range) {
      const { start, end } = assignment.range;
      if (start.surah.id === end.surah.id) {
        return `${start.surah.name} ${start.verse}-${end.verse}`;
      }
      return `${start.surah.name} ${start.verse} to ${end.surah.name} ${end.verse}`;
    }

    if (assignment.surah) {
      return `Surah ${assignment.surah.name}`;
    }

    return assignment.title;
  };

  // Get assignment title with line estimate for preview (lines shown ONLY in preview, not saved)
  const getAssignmentTitleForPreview = (assignment) => {
    const baseTitle = assignment.title;

    // Show line estimate if available (line-based assignments)
    if (assignment.metadata && assignment.metadata.estimatedLines) {
      return `${baseTitle} (~${assignment.metadata.estimatedLines} lines)`;
    }

    return baseTitle;
  };

  if (initialLoading) {
    return (
      <div className="create-assignment-container">
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading plan preview...</p>
        </div>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="create-assignment-container">
        <div className="header">
          <h1>Plan Data Not Found</h1>
          <Link to="/learning-plans" className="back-btn">
            ← Back to Plans
          </Link>
        </div>
        <div className="form-card">
          <div className="form-section" style={{textAlign: 'center', padding: '60px 20px'}}>
            <p style={{color: '#666'}}>Unable to load plan data. Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>Review Suggested Assignments</h1>
        <Link to="/learning-plans" className="back-btn">
          ← Back to Plans
        </Link>
      </div>

      {/* Plan Summary */}
      <div className="form-card" style={{marginBottom: '20px'}}>
        <div className="form-section">
          <h3>Learning Plan Summary</h3>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginTop: '10px'}}>
            <div>
              <strong>Student:</strong>
              <div style={{marginTop: '5px', color: '#666'}}>{studentName}</div>
            </div>
            <div>
              <strong>Goal:</strong>
              <div style={{marginTop: '5px', color: '#666'}}>
                {planData.goal.type === 'pages' && `Pages ${planData.goal.pages.from}-${planData.goal.pages.to} (${planData.goal.pages.total} pages)`}
                {planData.goal.type === 'verses' && `${planData.goal.verses.total} verses`}
                {planData.goal.type === 'juz' && `Juz ${planData.goal.juz.number}`}
                {planData.goal.type === 'surahs' && `${planData.goal.surahs.length} surahs`}
                {planData.goal.type === 'lines' && `~${planData.goal.lines.dailyLines} lines per day`}
              </div>
            </div>
            <div>
              <strong>Timeline:</strong>
              <div style={{marginTop: '5px', color: '#666'}}>
                {formatDate(planData.timeline.start)} to {formatDate(planData.timeline.end)}
              </div>
            </div>
            <div>
              <strong>Schedule:</strong>
              <div style={{marginTop: '5px', color: '#666'}}>
                {planData.schedule.daysPerWeek} days/week ({planData.schedule.specificDays.slice(0, 3).map(d => d.substring(0, 3)).join(', ')}
                {planData.schedule.specificDays.length > 3 && '...'})
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div style={{marginBottom: '20px'}}>
          {warnings.map((warning, index) => (
            <div
              key={index}
              className={`warning-message ${warning.severity}`}
              style={{
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '10px',
                backgroundColor: warning.severity === 'medium' ? '#fff3cd' : '#e7f3ff',
                border: `1px solid ${warning.severity === 'medium' ? '#ffc107' : '#9e9e9e'}`,
                color: warning.severity === 'medium' ? '#856404' : '#004085'
              }}
            >
              <strong>{warning.type === 'high_workload' ? '⚠️ High Workload:' : 'ℹ️ Note:'}</strong> {warning.message}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="error-message" style={{marginBottom: '20px', padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '8px'}}>
          {error}
        </div>
      )}

      {/* Assignments List */}
      <div className="form-card">
        <div className="form-section">
          <h3>Suggested Assignments ({assignments.length})</h3>
          <p style={{color: '#666', fontSize: '14px', marginBottom: '15px'}}>
            Review assignments below. You can adjust due dates (within plan timeline) or remove assignments if needed.
          </p>

          {assignments.length === 0 ? (
            <div style={{textAlign: 'center', padding: '40px', color: '#999'}}>
              No assignments. Add at least one to continue.
            </div>
          ) : (
            <div style={{display: 'flex', flexDirection: 'column', gap: '15px'}}>
              {assignments.map((assignment, index) => (
                <div
                  key={index}
                  style={{
                    padding: '15px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    backgroundColor: '#fafafa'
                  }}
                >
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px'}}>
                    <div style={{flex: 1}}>
                      <div style={{fontWeight: '500', marginBottom: '5px'}}>
                        {index + 1}. {getAssignmentTitleForPreview(assignment)}
                      </div>
                      <div style={{fontSize: '13px', color: '#666'}}>
                        {getAssignmentContent(assignment)}
                      </div>
                    </div>
                    <div style={{display: 'flex', gap: '8px'}}>
                      {assignment.range && (
                        <button
                          type="button"
                          onClick={() => handleEditVerseRange(index)}
                          style={{
                            padding: '5px 10px',
                            fontSize: '12px',
                            backgroundColor: '#6C5CE7',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Edit Range
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveAssignment(index)}
                        className="btn-danger"
                        style={{
                          padding: '5px 10px',
                          fontSize: '12px',
                          backgroundColor: '#f44336',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{fontSize: '12px', color: '#666', display: 'block', marginBottom: '5px'}}>
                      Due Date
                    </label>
                    <input
                      type="date"
                      value={new Date(assignment.dueDate).toISOString().split('T')[0]}
                      onChange={(e) => handleEditAssignment(index, 'dueDate', new Date(e.target.value))}
                      style={{
                        width: '100%',
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Assignment Button */}
          <button
            type="button"
            onClick={handleAddAssignment}
            style={{
              marginTop: '20px',
              padding: '12px',
              width: '100%',
              fontSize: '14px',
              backgroundColor: '#f5f5f5',
              color: '#6C5CE7',
              border: '2px dashed #6C5CE7',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f0ff';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#f5f5f5';
            }}
          >
            + Add Assignment
          </button>
        </div>
      </div>

      {/* Action Buttons */}
      <div style={{display: 'flex', gap: '10px', marginTop: '20px'}}>
        <button
          type="button"
          onClick={() => navigate('/learning-plans/new', {
            state: {
              formData: location.state // Pass back the original form data
            }
          })}
          className="btn-secondary"
          style={{flex: 1}}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={handleApprove}
          className="btn-primary"
          disabled={loading || assignments.length === 0}
          style={{flex: 2}}
        >
          {loading ? 'Creating Assignments...' : `Create Plan & ${assignments.length} Assignments`}
        </button>
      </div>

      {/* Edit Verse Range Modal */}
      {editingAssignment && (
        <EditVerseRangeModal
          assignment={editingAssignment.assignment}
          onSave={handleSaveVerseRange}
          onCancel={handleCancelEdit}
        />
      )}

      {/* Add Assignment Modal */}
      {isAddingAssignment && (
        <AddAssignmentModal
          planData={planData}
          onSave={handleSaveNewAssignment}
          onCancel={handleCancelAddAssignment}
        />
      )}
    </div>
  );
};

/**
 * Add Assignment Modal Component
 *
 * Allows teachers to manually add verse-range assignments to a learning plan during preview.
 * Currently supports only verse-range assignments (not pages, surahs, or lines goals).
 *
 * The new assignment:
 * - Gets appended to the end of the assignments list
 * - Auto-assigns due date (1 day after last assignment, or plan start)
 * - Inherits plan's assignmentType (new-memorization, revision, or recitation)
 * - Gets linked to the plan's student
 *
 * @param {Object} planData - The learning plan data (not yet saved)
 * @param {Function} onSave - Callback when assignment is created
 * @param {Function} onCancel - Callback when modal is closed
 */
const AddAssignmentModal = ({ planData, onSave, onCancel }) => {
  const allSurahs = QuranService.getAllSurahs();

  const [startSurahId, setStartSurahId] = useState('');
  const [startVerse, setStartVerse] = useState('');
  const [endSurahId, setEndSurahId] = useState('');
  const [endVerse, setEndVerse] = useState('');

  const handleSave = () => {
    // Find surah names
    const startSurah = allSurahs.find(s => s.id === startSurahId);
    const endSurah = allSurahs.find(s => s.id === endSurahId);

    if (!startSurah || !endSurah) {
      alert('Please select both start and end surahs');
      return;
    }

    // Basic validation
    if (!startVerse || !endVerse) {
      alert('Please enter verse numbers');
      return;
    }

    const startVerseNum = parseInt(startVerse);
    const endVerseNum = parseInt(endVerse);

    if (startVerseNum < 1 || startVerseNum > startSurah.verseCount) {
      alert(`Start verse must be between 1 and ${startSurah.verseCount} for ${startSurah.name}`);
      return;
    }

    if (endVerseNum < 1 || endVerseNum > endSurah.verseCount) {
      alert(`End verse must be between 1 and ${endSurah.verseCount} for ${endSurah.name}`);
      return;
    }

    // Validate range logic for same surah
    if (startSurahId === endSurahId && endVerseNum < startVerseNum) {
      alert(`End verse (${endVerseNum}) cannot be before start verse (${startVerseNum}) in the same surah`);
      return;
    }

    // Create assignment object matching the structure of plan-generated assignments
    const newAssignment = {
      range: {
        start: {
          surah: { id: startSurahId, name: startSurah.name },
          verse: startVerseNum
        },
        end: {
          surah: { id: endSurahId, name: endSurah.name },
          verse: endVerseNum
        }
      },
      title: startSurahId === endSurahId
        ? `${startSurah.name} ${startVerseNum}-${endVerseNum}`
        : `${startSurah.name} ${startVerseNum} to ${endSurah.name} ${endVerseNum}`,
      // Required fields for assignment creation
      type: planData.assignmentType, // Match the plan's assignment type
      students: [planData.studentId] // Link to the student
    };

    onSave(newAssignment);
  };

  // Get max verse for selected surah
  const getMaxVerse = (surahId) => {
    const surah = allSurahs.find(s => s.id === surahId);
    return surah ? surah.verseCount : 1;
  };

  // Calculate total verses in range
  const calculateVerseCount = () => {
    if (!startSurahId || !endSurahId || !startVerse || !endVerse) {
      return 0;
    }

    const startSurahNum = parseInt(startSurahId);
    const endSurahNum = parseInt(endSurahId);
    const startVerseNum = parseInt(startVerse);
    const endVerseNum = parseInt(endVerse);

    if (startSurahNum === endSurahNum) {
      return Math.max(0, endVerseNum - startVerseNum + 1);
    }

    // Multiple surahs - approximate
    const startSurah = allSurahs.find(s => s.id === startSurahId);
    const endSurah = allSurahs.find(s => s.id === endSurahId);

    if (!startSurah || !endSurah) return 0;

    let total = startSurah.verseCount - startVerseNum + 1;

    const direction = startSurahNum < endSurahNum ? 1 : -1;
    for (let currentSurah = startSurahNum + direction; currentSurah !== endSurahNum; currentSurah += direction) {
      const surah = allSurahs.find(s => s.id === String(currentSurah));
      if (surah) total += surah.verseCount;
    }

    total += endVerseNum;

    return total;
  };

  const verseCount = calculateVerseCount();

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '24px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ marginTop: 0, marginBottom: '8px', color: '#333' }}>
          Add New Assignment
        </h2>

        <p style={{ fontSize: '13px', color: '#666', marginBottom: '20px' }}>
          Select the verse range for the new assignment. It will be added at the end of the list.
        </p>

        {/* Verse Count Display */}
        {verseCount > 0 && (
          <div style={{
            marginBottom: '20px',
            padding: '8px 12px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#666'
          }}>
            Total: <strong>{verseCount} verse{verseCount !== 1 ? 's' : ''}</strong>
          </div>
        )}

        {/* Start Range */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>From</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Surah
              </label>
              <select
                value={startSurahId}
                onChange={(e) => setStartSurahId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="">Select surah...</option>
                {allSurahs.map((surah) => (
                  <option key={surah.id} value={surah.id}>
                    {surah.id}. {surah.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Verse
              </label>
              <input
                type="number"
                min="1"
                max={getMaxVerse(startSurahId)}
                value={startVerse}
                onChange={(e) => setStartVerse(e.target.value)}
                placeholder="1"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* End Range */}
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>To</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Surah
              </label>
              <select
                value={endSurahId}
                onChange={(e) => setEndSurahId(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="">Select surah...</option>
                {allSurahs.map((surah) => (
                  <option key={surah.id} value={surah.id}>
                    {surah.id}. {surah.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', color: '#666', marginBottom: '5px' }}>
                Verse
              </label>
              <input
                type="number"
                min="1"
                max={getMaxVerse(endSurahId)}
                value={endVerse}
                onChange={(e) => setEndVerse(e.target.value)}
                placeholder="1"
                style={{
                  width: '100%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              border: '1px solid #ddd',
              backgroundColor: 'white',
              color: '#666',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              padding: '10px 20px',
              borderRadius: '4px',
              border: 'none',
              backgroundColor: '#6C5CE7',
              color: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Add Assignment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignmentPreviewPage;
